/**
 * CallsReport — a bespoke per-recruiter calling dashboard (Reports → Calls tab).
 * Unlike the funnel/DQ reports this is NOT group-sliceable: it always renders one
 * row per recruiter, showing whether they're on a LIVE call right now (a pulsing
 * badge + a duration that ticks every second between polls) alongside their
 * call volume / talk-time / missed / avg / last-call over the selected range.
 *
 * Two timers: a 12s poll (re-fetch from the server) and a local 1s ticker that
 * only re-renders so live on-call durations advance smoothly between polls.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { PhoneCall, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import api from '../../../services/api';
import { CallsAnalytics, LiveCallState } from '../../types';
import { relTime } from '../../lib';
import BarRow from './BarRow';
import { fmtDuration, SummaryStat, Stat } from './StatBlocks';

interface CallsReportProps {
  from: string | null;
  to: string | null;
  /** Scope to one study (calls attributed via the contact's primary study). */
  studyId?: string | null;
}

/** Live elapsed seconds from a started_at ISO to `now` (ms). */
function liveSeconds(live: LiveCallState, now: number): number {
  if (!live.started_at) return 0;
  const started = new Date(live.started_at).getTime();
  if (Number.isNaN(started)) return 0;
  return Math.max(0, Math.floor((now - started) / 1000));
}

function LiveBadge({ live, now }: { live: LiveCallState; now: number }) {
  const Icon = live.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing;
  const dirLabel = live.direction === 'inbound' ? 'in' : live.direction === 'outbound' ? 'out' : '';
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
      </span>
      <Icon className="h-3.5 w-3.5" />
      <span>On a call{dirLabel ? ` (${dirLabel})` : ''}</span>
      {live.contact_first_name && <span className="opacity-80">· {live.contact_first_name}</span>}
      <span className="tabular-nums">{fmtDuration(liveSeconds(live, now))}</span>
    </span>
  );
}

export default function CallsReport({ from, to, studyId = null }: CallsReportProps) {
  const [data, setData] = useState<CallsAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Drives the 1s re-render so live durations tick between polls.
  const [now, setNow] = useState(() => Date.now());
  // Keep prior data on screen during the 12s background polls (no flicker).
  const firstLoad = useRef(true);

  // Fetch on mount + whenever the range changes, then poll every 12s. The alive
  // flag + interval cleanup prevent a stale range's response from landing.
  useEffect(() => {
    let alive = true;
    firstLoad.current = true;
    setLoading(true);
    const load = () => {
      api
        .get('/api/crm/analytics/calls', { params: { from: from || undefined, to: to || undefined, study_id: studyId || undefined } })
        .then((res) => {
          if (!alive) return;
          setData(res.data || null);
          setError(null);
        })
        .catch((err) => {
          if (!alive) return;
          // Don't blank an already-rendered dashboard on a transient poll error.
          if (firstLoad.current) setError(err?.response?.data?.error || 'Failed to load calls');
        })
        .finally(() => {
          if (!alive) return;
          firstLoad.current = false;
          setLoading(false);
        });
    };
    load();
    const poll = setInterval(load, 12000);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, [from, to, studyId]);

  // Local 1s ticker — only advances `now` so live on-call timers move smoothly.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const maxCalls = useMemo(
    () => Math.max(1, ...(data?.recruiters || []).map((r) => r.stats.total)),
    [data],
  );

  if (loading && !data) return <p className="p-4 text-sm text-gray-400">Loading calls…</p>;
  if (error && !data) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!data || data.recruiters.length === 0)
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No calls in this range yet.</p>;

  const { totals, recruiters, unattributed } = data;

  return (
    <div className="space-y-4">
      {data.study_scoped && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          Figures below are scoped to the selected study (calls attributed by each lead's current study). The live
          on-call state is network-wide.
        </p>
      )}
      {/* Summary strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryStat label="Total calls" value={totals.total.toLocaleString()} />
        <SummaryStat label="Talk time" value={fmtDuration(totals.talk_time_sec)} />
        <SummaryStat
          label="On a call now"
          value={totals.on_call_now.toLocaleString()}
          highlight={totals.on_call_now > 0}
        />
        <SummaryStat label="Recruiters online" value={totals.recruiters_online.toLocaleString()} />
      </div>

      {/* Per-recruiter list */}
      <div className="space-y-2">
        {recruiters.map((r) => {
          const { stats, live } = r;
          return (
            <div
              key={r.user_id}
              className={`rounded-xl border bg-white p-4 dark:bg-gray-800 ${
                live?.on_call
                  ? 'border-green-300 dark:border-green-800'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <h3 className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {r.name}
                  </h3>
                  {live?.on_call ? (
                    <LiveBadge live={live} now={now} />
                  ) : (
                    <span className="text-xs text-gray-400">Idle</span>
                  )}
                </div>
                <span className="flex-shrink-0 text-xs text-gray-400">
                  Last call {relTime(stats.last_call_at)}
                </span>
              </div>

              {/* Calls-per-recruiter comparative bar */}
              <div className="mt-3">
                <BarRow
                  label="Calls"
                  count={stats.total}
                  max={maxCalls}
                  hint={
                    stats.total > 0
                      ? `${stats.outbound.toLocaleString()} out · ${stats.inbound.toLocaleString()} in`
                      : null
                  }
                  color="bg-primary-500"
                />
              </div>

              {/* Range stats */}
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 px-2 text-xs text-gray-500 dark:text-gray-400 sm:grid-cols-4">
                <Stat icon={<PhoneOutgoing className="h-3.5 w-3.5" />} label="Outbound" value={stats.outbound.toLocaleString()} />
                <Stat icon={<PhoneIncoming className="h-3.5 w-3.5" />} label="Inbound" value={stats.inbound.toLocaleString()} />
                <Stat label="Talk time" value={fmtDuration(stats.talk_time_sec)} />
                <Stat label="Avg" value={stats.avg_sec > 0 ? fmtDuration(stats.avg_sec) : '—'} />
                <Stat label="Completed" value={stats.completed.toLocaleString()} />
                <Stat
                  label="Missed"
                  value={stats.missed.toLocaleString()}
                  emphasize={stats.missed > 0}
                />
              </div>
            </div>
          );
        })}
      </div>

      {unattributed.total > 0 && (
        <p className="px-1 text-xs text-gray-400">
          <PhoneCall className="mr-1 inline h-3 w-3" />
          {unattributed.total.toLocaleString()} call{unattributed.total === 1 ? '' : 's'} not attributed to a
          recruiter ({fmtDuration(unattributed.talk_time_sec)} talk time).
        </p>
      )}
    </div>
  );
}
